import { captureStore, useAnimationFrame } from '@playcastdotio/InputCapture';
import { useEffect, useSyncExternalStore } from 'react';
import { gamepadStore, MobileGamepadState } from './store';
import { XInputValues } from '@playcastdotio/CoreTypes';
import { cloneDeep } from 'lodash';
import { useState, useRef } from 'react';
import { useMobilePortrait } from './useMobilePortrait';

const ANIMATION_STEP = 3500;
const THUMBSTICK_SENSITIVITY = 0.5;
const stickMaxDistance = 25;

const gamepadButtonMap = {
  'right-ButtonSouth': XInputValues.A,
  'right-ButtonEast': XInputValues.B,
  'right-ButtonWest': XInputValues.X,
  'right-ButtonNorth': XInputValues.Y,
  'dpad-ButtonSouth': XInputValues.DPAD_DOWN,
  'dpad-ButtonEast': XInputValues.DPAD_RIGHT,
  'dpad-ButtonWest': XInputValues.DPAD_LEFT,
  'dpad-ButtonNorth': XInputValues.DPAD_UP,
  'left-ButtonMeta': XInputValues.BACK,
  'right-ButtonMeta': XInputValues.START,
  'left-ButtonBumper': XInputValues.LEFT_SHOULDER,
  'right-ButtonBumper': XInputValues.RIGHT_SHOULDER,

  'left-Trigger': XInputValues.LEFT_TRIGGER,
  'right-Trigger': XInputValues.RIGHT_TRIGGER,
  // Not detecting stick clicks for now
  // See https://github.com/PlayCastDotIo/playcast-apps/issues/564
  'right-StickClick': XInputValues.RIGHT_THUMB,
  'left-StickClick': XInputValues.LEFT_THUMB,
};

const calcXY = (start: { x: number; y: number }, current: { x: number; y: number }) => {
  const circleRadius = 32767;

  // Calculate the distance from the start of the touch to the current position
  const deltaX = (current.x - start.x) * THUMBSTICK_SENSITIVITY;
  const deltaY = (current.y - start.y) * THUMBSTICK_SENSITIVITY;
  const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  // If distance exceeds max distance, scale it down
  const clampedDistance = Math.min(distance, stickMaxDistance);
  const scaleFactor = clampedDistance / distance;

  // Apply scaling factor to deltaX and deltaY
  const scaledX = deltaX * scaleFactor;
  const scaledY = deltaY * scaleFactor * -1;

  // Calculate new coordinates within the circle radius
  const adjustedX = Math.round(circleRadius * (scaledX / stickMaxDistance));
  const adjustedY = Math.round(circleRadius * (scaledY / stickMaxDistance));

  return { x: adjustedX, y: adjustedY };
};

// Function to animate the return to neutral position
const springBack = (currentValue: number, step: number): number => {
  if (Math.abs(currentValue) < step) {
    return 0;
  }
  return currentValue - Math.sign(currentValue) * step;
};

export const MobileGamepadInput = () => {
  const [, setRender] = useState(0);
  const event = useSyncExternalStore(captureStore.subscribeEvent, captureStore.getEvent);
  const gamepadStateRef = useRef(gamepadStore.getGamepad());
  const thumbstickActiveRef = useRef<{ left: boolean; right: boolean }>({ left: false, right: false });
  const isMobilePortrait = useMobilePortrait();

  const updateGamepadState = (state: MobileGamepadState) => {
    gamepadStore.updateGamepad(state);
    gamepadStateRef.current = state;
    setRender((prev) => prev + 1);
  };

  // Unused for now, see above
  // Once we can detect force, stick clicks might begin or end on any
  // touch event, not just start and end (because pressure could change
  // any number of times during a touch)
  // const stickClickThreshold = 0.5;

  useAnimationFrame(() => {
    const state = gamepadStateRef.current;
    let needsUpdate = false;

    if (!thumbstickActiveRef.current.left) {
      if (state.sThumbLX !== 0 || state.sThumbLY !== 0) {
        state.sThumbLX = springBack(state.sThumbLX, ANIMATION_STEP);
        state.sThumbLY = springBack(state.sThumbLY, ANIMATION_STEP);
        needsUpdate = true;
      }
    }

    if (!thumbstickActiveRef.current.right) {
      if (state.sThumbRX !== 0 || state.sThumbRY !== 0) {
        state.sThumbRX = springBack(state.sThumbRX, ANIMATION_STEP);
        state.sThumbRY = springBack(state.sThumbRY, ANIMATION_STEP);
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      updateGamepadState({ ...state });
    }
  });

  useEffect(() => {
    if (event && event?.type.substring(0, 5) === 'touch') {
      const state = gamepadStore.getGamepad();
      const newGamepadState: MobileGamepadState = cloneDeep(state);

      const touches = (event as TouchEvent).changedTouches;

      switch (event.type) {
        case 'touchstart':
          for (let i = 0; i < touches.length; i++) {
            const t = touches[i].target as HTMLElement;
            const buttonId = t.id as keyof typeof gamepadButtonMap;

            // If thumbstick is touched, set it as active
            if (t.id === 'left-Stick' || t.id === 'right-Stick') {
              thumbstickActiveRef.current[t.id === 'left-Stick' ? 'left' : 'right'] = true;
            }

            // Retaining your existing logic
            if (gamepadButtonMap[buttonId]) {
              newGamepadState.wButtons |= gamepadButtonMap[buttonId];
            }
            if (t.id === 'left-Stick' || t.id === 'right-Stick') {
              newGamepadState.touches[touches[i].identifier] = {
                identifier: touches[i].identifier,
                target: t,
                start: {
                  x: touches[i].clientX,
                  y: touches[i].clientY,
                },
              };
              thumbstickActiveRef.current[t.id === 'left-Stick' ? 'left' : 'right'] = true;
            }
            if (t.id === 'left-ButtonTrigger' || t.id === 'right-ButtonTrigger') {
              const trigger = t.id === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
              newGamepadState[trigger] = 255;
            }

            // New logic to check for overlapping touches between directional buttons
            const touchX = touches[i].clientX;
            const touchY = touches[i].clientY;

            const buttonNorth = document.getElementById('dpad-ButtonNorth');
            const buttonSouth = document.getElementById('dpad-ButtonSouth');
            const buttonEast = document.getElementById('dpad-ButtonEast');
            const buttonWest = document.getElementById('dpad-ButtonWest');

            const rectNorth = buttonNorth?.getBoundingClientRect();
            const rectSouth = buttonSouth?.getBoundingClientRect();
            const rectEast = buttonEast?.getBoundingClientRect();
            const rectWest = buttonWest?.getBoundingClientRect();

            // Function to check if a touch is within a button's boundary
            const isInButton = (rect: DOMRect | undefined, touchX: number, touchY: number) => {
              return rect && touchX >= rect.left && touchX <= rect.right && touchY >= rect.top && touchY <= rect.bottom;
            };

            const inNorth = isInButton(rectNorth, touchX, touchY);
            const inSouth = isInButton(rectSouth, touchX, touchY);
            const inEast = isInButton(rectEast, touchX, touchY);
            const inWest = isInButton(rectWest, touchX, touchY);

            // Trigger both buttons if touch overlaps between two directions
            if (inNorth && inWest) {
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonNorth'];
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonWest'];
            } else if (inNorth && inEast) {
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonNorth'];
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonEast'];
            } else if (inSouth && inWest) {
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonSouth'];
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonWest'];
            } else if (inSouth && inEast) {
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonSouth'];
              newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonEast'];
            } else {
              // Single button checks
              if (inNorth) {
                newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonNorth'];
              }
              if (inSouth) {
                newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonSouth'];
              }
              if (inEast) {
                newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonEast'];
              }
              if (inWest) {
                newGamepadState.wButtons |= gamepadButtonMap['dpad-ButtonWest'];
              }
            }

            // Updating the gamepad state after processing the touches
            gamepadStore.updateGamepad(newGamepadState);
            gamepadStateRef.current = newGamepadState;
          }
          break;

        case 'touchmove':
          for (let i = 0; i < touches.length; i++) {
            const t = touches[i].target as HTMLElement;
            const touchIdentifier = touches[i].identifier;
            if ((t.id === 'left-Stick' || t.id === 'right-Stick') && newGamepadState.touches[touchIdentifier]) {
              const { x, y } = calcXY(newGamepadState.touches[touchIdentifier].start, {
                x: touches[i].clientX,
                y: touches[i].clientY,
              });
              if (t.id === 'left-Stick') {
                if (isMobilePortrait) {
                  newGamepadState.sThumbLY = x;
                  newGamepadState.sThumbLX = -y;
                } else {
                  newGamepadState.sThumbLX = x;
                  newGamepadState.sThumbLY = y;
                }
              } else {
                if (isMobilePortrait) {
                  newGamepadState.sThumbRY = x;
                  newGamepadState.sThumbRX = -y;
                } else {
                  newGamepadState.sThumbRX = x;
                  newGamepadState.sThumbRY = y;
                }
              }
            }
          }
          gamepadStore.updateGamepad(newGamepadState);
          gamepadStateRef.current = newGamepadState;
          break;

        // For our purposes, end and cancel are the same
        case 'touchend':
        case 'touchcancel':
          for (let i = 0; i < touches.length; i++) {
            const t = touches[i].target as HTMLElement;
            const buttonId = t.id as keyof typeof gamepadButtonMap;

            // If thumbstick is released, mark it as inactive
            if (t.id === 'left-Stick' || t.id === 'right-Stick') {
              thumbstickActiveRef.current[t.id === 'left-Stick' ? 'left' : 'right'] = false;
            }

            // Clear the specific button that was pressed
            if (gamepadButtonMap[buttonId]) {
              newGamepadState.wButtons &= ~gamepadButtonMap[buttonId];
            }

            // Handle dual-button press clear logic (North+West, etc.)
            const touchX = touches[i].clientX;
            const touchY = touches[i].clientY;

            const buttonNorth = document.getElementById('dpad-ButtonNorth');
            const buttonSouth = document.getElementById('dpad-ButtonSouth');
            const buttonEast = document.getElementById('dpad-ButtonEast');
            const buttonWest = document.getElementById('dpad-ButtonWest');

            const rectNorth = buttonNorth?.getBoundingClientRect();
            const rectSouth = buttonSouth?.getBoundingClientRect();
            const rectEast = buttonEast?.getBoundingClientRect();
            const rectWest = buttonWest?.getBoundingClientRect();

            const inNorth =
              rectNorth && touchX >= rectNorth.left && touchX <= rectNorth.right && touchY >= rectNorth.top && touchY <= rectNorth.bottom;
            const inSouth =
              rectSouth && touchX >= rectSouth.left && touchX <= rectSouth.right && touchY >= rectSouth.top && touchY <= rectSouth.bottom;
            const inEast =
              rectEast && touchX >= rectEast.left && touchX <= rectEast.right && touchY >= rectEast.top && touchY <= rectEast.bottom;
            const inWest =
              rectWest && touchX >= rectWest.left && touchX <= rectWest.right && touchY >= rectWest.top && touchY <= rectWest.bottom;

            // If touch was between North and West, clear both on touchend
            if (inNorth && inWest) {
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonNorth'];
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonWest'];
            }
            // If touch was between North and East, clear both on touchend
            else if (inNorth && inEast) {
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonNorth'];
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonEast'];
            }
            // If touch was between South and West, clear both on touchend
            else if (inSouth && inWest) {
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonSouth'];
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonWest'];
            }
            // If touch was between South and East, clear both on touchend
            else if (inSouth && inEast) {
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonSouth'];
              newGamepadState.wButtons &= ~gamepadButtonMap['dpad-ButtonEast'];
            }

            // Ensure clearing any stuck buttons
            // This ensures that all directional buttons are cleared at the end of the touch
            newGamepadState.wButtons &= ~(
              gamepadButtonMap['dpad-ButtonNorth'] |
              gamepadButtonMap['dpad-ButtonSouth'] |
              gamepadButtonMap['dpad-ButtonEast'] |
              gamepadButtonMap['dpad-ButtonWest']
            );

            // Clear the thumbstick or trigger buttons as before
            if (t.id === 'left-Stick' || t.id === 'right-Stick') {
              delete newGamepadState.touches[touches[i].identifier];
              thumbstickActiveRef.current[t.id === 'left-Stick' ? 'left' : 'right'] = false;
            }
            if (t.id === 'left-ButtonTrigger' || t.id === 'right-ButtonTrigger') {
              const trigger = t.id === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
              newGamepadState[trigger] = 0;
            }

            // Update the gamepad state after clearing the buttons
            gamepadStore.updateGamepad(newGamepadState);
            gamepadStateRef.current = newGamepadState;
          }
          break;
      }
    }
  }, [event, isMobilePortrait]);

  return null;
};
